import { request } from '@/api/request'
import type { MessageView } from '@/api/messagesApi'

/** Sidebar / KPI counts from `GET /tickets/counts`. */
export interface TicketCounts {
  allOpen?: number
  assignedToMe?: number
  slaAtRisk?: number
  gxpImpacting?: number
  escalated?: number
  critical?: number
  byProduct?: Record<string, number>
}

export interface Ref {
  id: string
  name: string
}

export interface TicketSla {
  dueAt: string | null
  firstResponseDueAt: string | null
  firstRespondedAt: string | null
  warnAt: string | null
  clockRunning: boolean
  /** Paused clocks: business minutes left when the clock stopped (from the engine). Null while running. */
  remainingMins?: number | null
  breached: boolean
}

export interface LinkedRecordStage {
  name: string
  /** `done` | `blocked` | `pending` (anything else renders as pending). */
  status: string
  summary?: string | null
}

export interface LinkedRecordSnapshot {
  key?: string
  type?: string
  title?: string | null
  subtitle?: string | null
  status?: string | null
  phi?: boolean
  stages?: LinkedRecordStage[]
}

export interface LinkedRecord {
  product: string
  type: string
  key: string
  url: string | null
  snapshot?: LinkedRecordSnapshot | null
  snapshotAt?: string | null
}

export interface LinkedRecordInput {
  product: string
  type: string
  key: string
  url?: string
}

/** One row of `GET /tickets`. */
export interface TicketListItem {
  id: string
  number: number
  key: string
  subject: string
  type: string
  priority: string
  status: string
  channel: string
  customer: { id: string; name: string; slug: string }
  /** The client's branch (site), e.g. Rochester. */
  tenant: { id: string; name: string; code: string } | null
  environment: { id: string; name: string; kind: string; isValidated: boolean } | null
  product: { id: string; key: string; name: string }
  component: { id: string; key: string; name: string } | null
  assignee: { id: string; name: string; initials: string; email: string } | null
  reporter: { id: string; name: string; email: string; role: string | null } | null
  gxpImpact: string
  linkedRecord: LinkedRecord | null
  sla: TicketSla
  version: number
  createdAt: string
  updatedAt: string
  resolvedAt: string | null
}

export interface TicketResolution {
  sequence: number
  resolvedBy: Ref
  resolvedAt: string
  timeZone: string
  reason: string
  summary: string
  category: string
  rootCause: string | null
  deviationDecision: string | null
}

export interface TicketCsat {
  rating: number
  comment: string | null
  by: string | null
  createdAt: string
}

/** `GET /tickets/:id`. Agent-only fields are absent for customer principals. */
export interface TicketDetail extends TicketListItem {
  watcherContactIds?: string[]
  team?: Ref | null
  teamId?: string | null
  assigneeId?: string | null
  gxpNote?: string | null
  formRef: string | null
  formVersion: string | null
  deviationRef?: string | null
  deviationDecision?: string | null
  externalRef?: string | null
  /** Intake context; `receivedVia` / `receivedAt` for agent-logged tickets. */
  context?: TicketContext | null
  followUpOf: { id: string; key: string } | null
  duplicateOf: { id: string; key: string } | null
  slaPolicyId?: string | null
  slaPolicy?: { name: string; priority: string; resolutionMins: number; firstResponseMins: number } | null
  reopenUntil: string | null
  closedAt: string | null
  resolution: TicketResolution | null
  csat: TicketCsat | null
  // Customers get only customerMessages + attachments (no internal totals).
  counts: { messages?: number; customerMessages: number; attachments: number; auditEntries?: number }
  internalNotes?: number
  /** Agents: statuses a PATCH may move this ticket to (from the API state machine; resolve/reopen excluded). */
  allowedTransitions?: string[]
}

export const RECEIVED_VIA = ['phone', 'email', 'teams', 'in_person', 'other'] as const
export type ReceivedVia = (typeof RECEIVED_VIA)[number]

export interface TicketContext {
  receivedVia?: ReceivedVia
  /** ISO instant. */
  receivedAt?: string
  [key: string]: unknown
}

/** `POST /tickets` answers the detail plus the description message's id (attach intake files to it). */
export type CreatedTicket = TicketDetail & { firstMessageId: string }

/** `GET /sla-policies/applicable` — the policy a new ticket would get. */
export interface ApplicableSlaPolicy {
  policyId: string
  name: string
  firstResponseMins: number
  resolutionMins: number
  calendar: { name: string; is24x7: boolean; timeZone: string }
}

export interface TicketPage {
  items: TicketListItem[]
  nextCursor: string | null
  total: number
}

export interface CreateTicketInput {
  subject: string
  description: string
  type: string
  priority: string
  productId: string
  componentId?: string
  customerId?: string
  /** Agents: a tenant of the customer; omitted = the reporter contact's tenant. Ignored for customers. */
  tenantId?: string
  environmentId?: string
  reporter?: { contactId: string } | { email: string; name: string; role?: string }
  watcherContactIds?: string[]
  assigneeId?: string
  channel?: string
  gxpImpact?: string
  formRef?: string
  formVersion?: string
  linkedRecord?: LinkedRecordInput
  /** Agents: how and when the request was received (ISO `receivedAt`, not in the future). */
  context?: { receivedVia: ReceivedVia; receivedAt: string }
  /** Agents: internal GxP assessment note. */
  gxpNote?: string
}

export interface UpdateTicketInput {
  subject?: string
  type?: string
  priority?: string
  status?: string
  assigneeId?: string | null
  componentId?: string | null
  environmentId?: string | null
  /** Agents only; null clears it. */
  tenantId?: string | null
  gxpImpact?: string
  gxpNote?: string | null
  formRef?: string | null
  formVersion?: string | null
  reason?: string
}

export interface ResolveTicketInput {
  reason: string
  summary: string
  category: string
  rootCause?: string
  gxpImpact: string
  deviationDecision?: string
  deviationRef?: string
  duplicateOfId?: string
  timeZone?: string
}

export interface EscalateDeviationInput {
  decision: string
  deviationRef?: string
}

/** One audit-chain row (`GET /tickets/:id/audit`). */
export interface AuditEntry {
  id: string
  seq: number
  eventType: string
  field: string | null
  oldValue: unknown
  newValue: unknown
  reason: string | null
  actorId: string | null
  actorKind: string
  at: string
  metadata: Record<string, unknown> | null
  prevHash: string | null
  hash: string
  visibility: string
}

export interface AuditVerifyResult {
  ticketId: string
  valid: boolean
  length: number
  headSeq: number | null
  headHash: string | null
  brokenAtSeq: number | null
  reason: string | null
  anchor?: { day: string; anchoredSeq: number; matches: boolean; headsHashValid: boolean } | null
}

export type ExportAudience = 'customer' | 'internal'

/** `GET /tickets/:id/export` — the JSON the PDF is rendered from. */
export interface TicketExport {
  ticket: TicketDetail
  messages: MessageView[]
  resolution: TicketResolution | null
  attachments: { id: string; fileName: string; mimeType: string; sizeBytes: number; visibility?: string }[]
  /** Chain state at export time; the API names the check `valid` (older drafts: `verified`). */
  audit: { headHash: string | null; headSeq?: number | null; length?: number; valid?: boolean; verified?: boolean }
  generatedAt: string
  generatedBy: string | { id: string; name: string; email?: string }
  audience?: ExportAudience
  csat?: TicketCsat | null
}

const json = (body: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(body) })
const enc = encodeURIComponent

export const ticketsApi = {
  counts: (): Promise<TicketCounts> => request<TicketCounts>('/tickets/counts'),

  /** `params` is the query string built by `toApiParams()` in src/lib/queueFilters.ts. */
  list: (params: URLSearchParams): Promise<TicketPage> => {
    const qs = params.toString()
    return request<TicketPage>(`/tickets${qs ? `?${qs}` : ''}`)
  },

  get: (id: string): Promise<TicketDetail> => request<TicketDetail>(`/tickets/${enc(id)}`),

  create: (input: CreateTicketInput): Promise<CreatedTicket> => request<CreatedTicket>('/tickets', json(input)),

  /** Agents (tickets.create): the SLA policy a new ticket would get, or null. */
  applicableSlaPolicy: async (q: { priority: string; customerId: string; productId: string }): Promise<ApplicableSlaPolicy | null> =>
    (await request<ApplicableSlaPolicy | undefined>(
      `/sla-policies/applicable?${new URLSearchParams(q).toString()}`,
    )) ?? null,

  /** Optimistic concurrency: a stale `version` answers 409. */
  update: (id: string, version: number, input: UpdateTicketInput): Promise<TicketDetail> =>
    request<TicketDetail>(`/tickets/${enc(id)}`, {
      method: 'PATCH',
      headers: { 'If-Match': String(version) },
      body: JSON.stringify(input),
    }),

  resolve: (id: string, input: ResolveTicketInput): Promise<TicketDetail> =>
    request<TicketDetail>(`/tickets/${enc(id)}/resolve`, json(input)),

  reopen: (id: string, reason: string): Promise<TicketDetail> =>
    request<TicketDetail>(`/tickets/${enc(id)}/reopen`, json({ reason })),

  linkRecord: (id: string, input: LinkedRecordInput): Promise<TicketDetail> =>
    request<TicketDetail>(`/tickets/${enc(id)}/link-record`, json(input)),

  escalateDeviation: (id: string, input: EscalateDeviationInput): Promise<TicketDetail> =>
    request<TicketDetail>(`/tickets/${enc(id)}/escalate-deviation`, json(input)),

  exportThread: (id: string, audience: ExportAudience): Promise<TicketExport> =>
    request<TicketExport>(`/tickets/${enc(id)}/export?audience=${audience}`),

  audit: (id: string): Promise<AuditEntry[]> => request<AuditEntry[]>(`/tickets/${enc(id)}/audit`),

  verifyAudit: (id: string): Promise<AuditVerifyResult> =>
    request<AuditVerifyResult>(`/tickets/${enc(id)}/audit/verify`),
}
